import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { companyInfo } from '../../mock';
import Footer from '../../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-dark)' }}>
      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(9, 166, 163, 0.2)', backgroundColor: 'rgba(36, 37, 38, 0.95)' }}>
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-accent-teal hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Privacy Policy</h1>
        
        <div className="text-neutral-silver leading-relaxed space-y-6">
          <p className="text-lg">Last updated: December 2024</p>

          <p>
            At NT Studios, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
          </p>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Personal Information</h3>
            <p className="mb-4">We collect information that you provide directly to us:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Name and email address (for account creation)</li>
              <li>Payment information (processed securely via Razorpay)</li>
              <li>Project details and requirements (for custom services)</li>
              <li>Communication history with our support team</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>IP address and browser type</li>
              <li>Device information and operating system</li>
              <li>Pages visited and time spent on site</li>
              <li>Referring website addresses</li>
              <li>Usage patterns of our AI tools</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Content You Generate</h3>
            <p>
              When you use our AI tools (Email Generator, Content Brief Generator, Competitor Analysis), we may store:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Input data you provide to generate content</li>
              <li>Generated outputs (stored securely in Firebase)</li>
              <li>Usage logs for daily limits and analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">2. How We Use Your Information</h2>
            <p className="mb-4">We use the collected information for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Providing and maintaining our services</li>
              <li>Processing payments and managing subscriptions</li>
              <li>Sending service-related notifications and updates</li>
              <li>Responding to your inquiries and support requests</li>
              <li>Improving our AI tools and user experience</li>
              <li>Analyzing usage patterns to enhance features</li>
              <li>Detecting and preventing fraud or abuse</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">3. Data Storage and Security</h2>
            <p className="mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Data stored securely on <strong className="text-accent-teal">Firebase (Google Cloud)</strong></li>
              <li>Encrypted connections (HTTPS/SSL)</li>
              <li>Secure payment processing via <strong className="text-accent-teal">Razorpay</strong></li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication</li>
              <li>Encrypted data at rest and in transit</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">4. Third-Party Services</h2>
            <p className="mb-4">We use trusted third-party services:</p>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Firebase (Google)</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Authentication and user management</li>
              <li>Database storage (Firestore)</li>
              <li>Subject to Google's Privacy Policy</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Razorpay</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Payment processing</li>
              <li>We do not store credit card information</li>
              <li>Subject to Razorpay's Privacy Policy</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">OpenAI</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Powers our AI content generation tools</li>
              <li>Input data processed to generate outputs</li>
              <li>Subject to OpenAI's Privacy Policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">5. Cookies and Tracking</h2>
            <p className="mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Maintain your login session</li>
              <li>Remember your preferences</li>
              <li>Analyze site traffic and usage</li>
              <li>Improve user experience</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings. Disabling cookies may limit some functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">6. Data Sharing and Disclosure</h2>
            <p className="mb-4">We do NOT sell your personal information. We may share data only in these circumstances:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>With your consent</strong> for specific purposes</li>
              <li><strong>Service providers</strong> who assist in operations (Firebase, Razorpay, OpenAI)</li>
              <li><strong>Legal requirements</strong> to comply with laws or court orders</li>
              <li><strong>Business transfers</strong> in case of merger or acquisition</li>
              <li><strong>Protection</strong> of our rights, safety, or property</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">7. Your Privacy Rights</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access</strong> your personal data</li>
              <li><strong>Correct</strong> inaccurate information</li>
              <li><strong>Delete</strong> your account and data</li>
              <li><strong>Export</strong> your data</li>
              <li><strong>Opt-out</strong> of marketing communications</li>
              <li><strong>Withdraw consent</strong> for data processing</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@ntstudios.com" className="text-accent-teal hover:underline">privacy@ntstudios.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">8. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide services. After account deletion:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
              <li>Personal data deleted within 30 days</li>
              <li>Some data retained for legal/tax purposes (as required by law)</li>
              <li>Anonymized analytics data may be retained indefinitely</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">9. Children's Privacy</h2>
            <p>
              Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children. If you believe we have collected data from a child, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">10. International Data Transfers</h2>
            <p>
              Your data may be transferred to and stored on servers located outside your country. We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of significant changes via email or prominent notice on our website. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">12. Contact Us</h2>
            <p className="mb-4">
              For questions about this Privacy Policy or to exercise your rights:
            </p>
            <ul className="list-none space-y-2">
              <li>Email: <a href="mailto:privacy@ntstudios.com" className="text-accent-teal hover:underline">privacy@ntstudios.com</a></li>
              <li>Contact Form: <Link to="/#contact" className="text-accent-teal hover:underline">ntstudios.com/#contact</Link></li>
            </ul>
          </section>

          <div className="mt-12 p-6 rounded-lg" style={{ backgroundColor: 'rgba(9, 166, 163, 0.1)', border: '1px solid rgba(9, 166, 163, 0.3)' }}>
            <p className="text-white font-semibold mb-2">Your Privacy Matters</p>
            <p>
              At NT Studios, protecting your privacy is fundamental to our values. We are committed to transparency and giving you control over your data.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
